import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@insolech/ui';
import {
  orderMutations,
  orderQueries,
  type DirectBillingPaymentMethod,
} from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * MarkPaidDialog
 *
 * Admin-only modal replacing the one-click "Mark as paid" button on
 * direct-billed orders. The click used to be a bare column UPDATE that
 * recorded no actor, no method and no audit row (backlog item 29); the
 * dialog now requires a payment method and calls the
 * admin_mark_direct_billing_paid RPC, which stamps a server-side
 * timestamp, records the acting admin, sets client_payment_status and
 * writes audit_log.
 *
 * Modal scaffolding mirrors EditOrderLinePriceDialog so the visual
 * language stays consistent.
 */

const METHOD_OPTIONS: Array<{ value: DirectBillingPaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'card_offline', label: 'Card (offline terminal)' },
  { value: 'other', label: 'Other' },
];

interface Props {
  open: boolean;
  orderId: string;
  orderNumber: string;
  totalAedLabel: string;
  /** Whether a single-order Stripe checkout session exists: after marking
   *  paid we expire it so the link in the patient's inbox stops being
   *  payable (item 30). */
  hadStripeSession: boolean;
  onClose: () => void;
}

export function MarkPaidDialog({
  open,
  orderId,
  orderNumber,
  totalAedLabel,
  hadStripeSession,
  onClose,
}: Props) {
  const qc = useQueryClient();

  const [method, setMethod] = React.useState<DirectBillingPaymentMethod | ''>('');
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Set when the payment WAS recorded but the Stripe link could not be
  // cancelled: the dialog stays open to show it, because a link that can
  // still collect a second payment is exactly what the admin must know.
  const [expireWarning, setExpireWarning] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setMethod('');
    setNote('');
    setSaveError(null);
    setSaving(false);
    setExpireWarning(null);
  }, [open, orderId]);

  const onConfirm = async () => {
    setSaveError(null);
    if (!method) {
      setSaveError('Select how the payment was collected.');
      return;
    }
    setSaving(true);
    try {
      await orderMutations.adminMarkDirectBillingPaid(supabase, {
        orderId,
        method,
        note: note.trim() || undefined,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(orderId) }),
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.lists() }),
      ]);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setSaving(false);
      return;
    }
    // The money is recorded. Now kill the still-live payment link, and be
    // LOUD if that fails - the webhook's double-collection alarm exists as
    // the backstop, but the admin standing here can also chase it.
    if (hadStripeSession) {
      try {
        await orderMutations.expireOrderCheckout(supabase, orderId);
      } catch (e) {
        setSaving(false);
        setExpireWarning(
          `The order is marked paid, but the Stripe payment link could NOT be cancelled and may still be live: ${
            e instanceof Error ? e.message : String(e)
          }. If the patient pays it, every admin is alerted automatically - but consider cancelling the session in the Stripe dashboard now.`,
        );
        return;
      }
    }
    onClose();
  };

  return (
    <Modal open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Mark order {orderNumber} as paid</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          <p className="text-sm text-text-muted">
            Records that {totalAedLabel} was collected from the patient outside
            Stripe. The payment method becomes part of the bookkeeping record
            and cannot be edited afterwards.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="markpaid-method">
              Payment method
            </label>
            <select
              id="markpaid-method"
              className="w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
              value={method}
              onChange={(e) => setMethod(e.target.value as DirectBillingPaymentMethod | '')}
            >
              <option value="">Select…</option>
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="markpaid-note">
              Reference / note (optional)
            </label>
            <Input
              id="markpaid-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. bank transfer reference"
            />
          </div>
          {saveError ? (
            <Alert tone="error" title="Could not mark as paid">
              {saveError}
            </Alert>
          ) : null}
          {expireWarning ? (
            <Alert tone="warning" title="Payment link may still be live">
              {expireWarning}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {expireWarning ? (
            <Button onClick={onClose}>Understood</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void onConfirm()} disabled={saving || !method}>
                {saving ? 'Marking as paid…' : 'Mark as paid'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
